import { useState, useEffect, useCallback } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useProjectSiteNavigation } from '@/shared/lib';

interface LocationState {
  consignmentIds?: string[];
}

export function useConsignmentNavigation() {
  const { navigateWithContext } = useProjectSiteNavigation();
  const { consignmentId } = useParams<{ consignmentId: string }>();
  const location = useLocation();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [consignmentIds, setConsignmentIds] = useState<string[]>([]);

  useEffect(() => {
    if (!consignmentId) return;

    const state = location.state as LocationState;
    const stateIds = state?.consignmentIds;

    // Multiple consignments mode - from location state
    if (stateIds?.length) {
      setConsignmentIds(stateIds);
      const index = stateIds.indexOf(consignmentId);
      setCurrentIndex(Math.max(0, index));
      return;
    }

    // Single consignment mode - no state or empty state
    setConsignmentIds([consignmentId]);
    setCurrentIndex(0);
  }, [location.state, consignmentId]);

  const currentConsignmentId = consignmentIds[currentIndex] || consignmentId;
  const isMultipleConsignments = consignmentIds.length > 1;

  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      navigateWithContext(`consignments/${consignmentIds[newIndex]}/edit`, {
        state: { consignmentIds },
        replace: true
      });
    }
  }, [currentIndex, navigateWithContext, consignmentIds]);

  const handleNext = useCallback(() => {
    if (currentIndex < consignmentIds.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      navigateWithContext(`consignments/${consignmentIds[newIndex]}/edit`, {
        state: { consignmentIds },
        replace: true
      });
    }
  }, [currentIndex, navigateWithContext, consignmentIds]);

  const handleBack = useCallback(() => {
    navigateWithContext('requests');
  }, [navigateWithContext]);

  return {
    // state
    currentConsignmentId,
    isMultipleConsignments,
    currentIndex,
    // derived state
    totalConsignments: consignmentIds.length,
    canGoPrevious: currentIndex > 0,
    canGoNext: currentIndex < consignmentIds.length - 1,
    // actions
    handlePrevious,
    handleNext,
    handleBack
  };
}
